import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ReceiptUpload from "@/components/ReceiptUpload";
import ConfirmCard from "@/components/ConfirmCard";
import ReceiptsTable from "@/components/ReceiptsTable";
import Stats from "@/components/Stats";
import ManageSubscription from "@/components/ManageSubscription";
import ForwardEmailCard from "@/components/ForwardEmailCard";
import ReferralCard from "@/components/ReferralCard";
import ThemeToggle from "@/components/ThemeToggle";
import CountUp from "@/components/CountUp";
import { ScanLine, Download, Crown, LogOut, ChevronDown, LayoutDashboard, FileText, FileSpreadsheet, FileType } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [usage, setUsage] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    const [r, d, u] = await Promise.all([
      api.get("/receipts"),
      api.get("/dashboard"),
      api.get("/usage"),
    ]);
    setReceipts(r.data);
    setDashboard(d.data);
    setUsage(u.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Non-subscribers always see Light; Dark Pro is a paid perk.
  useEffect(() => {
    if (usage && !usage.subscribed) document.documentElement.classList.remove("dark");
  }, [usage]);

  // Handle Stripe success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sid}`);
        if (res.data.payment_status === "paid") {
          toast.success("Welcome to Pro! Unlimited receipts unlocked.");
          window.history.replaceState(null, "", "/dashboard");
          refresh();
          return;
        }
        if (res.data.status === "expired" || attempts >= 5) return;
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (e) { /* ignore */ }
    };
    poll();
  }, [refresh]);

  const saveReceipt = async (payload) => {
    setSaving(true);
    try {
      await api.post("/receipts", payload);
      setExtracted(null);
      await refresh();
      toast.success("Receipt saved");
    } catch (err) {
      if (err.response?.status === 402) toast.error("Free limit reached — upgrade to continue.");
      else toast.error("Could not save receipt");
    } finally {
      setSaving(false);
    }
  };

  const exportFile = async (path, filename, label) => {
    try {
      const res = await fetch(`${API}/receipts/${path}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} downloaded`);
    } catch (e) { toast.error("Export failed"); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const pct = usage ? Math.min(100, (usage.month_count / usage.limit) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brand-icon-gold.png" alt="Receipt Republic" className="h-9 w-9 object-contain rounded-lg" />
            <span className="font-heading font-extrabold text-lg tracking-tight">Receipt Republic</span>
          </div>
          <div className="flex items-center gap-3">
            {usage?.subscribed && <ThemeToggle />}
            {usage?.subscribed ? (
              <ManageSubscription usage={usage} onChanged={refresh} />
            ) : (
              <Button data-testid="upgrade-nav-button" onClick={() => navigate("/upgrade")} variant="outline" size="sm" className="rounded-xl border-primary/30 text-primary hover:bg-primary/10">
                <Crown className="h-4 w-4 mr-1.5" /> Upgrade
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-full hover:bg-secondary pr-2 pl-1 py-1 transition-colors">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled className="opacity-100">
                  <div className="text-xs"><p className="font-semibold">{user?.name}</p><p className="text-muted-foreground">{user?.email}</p></div>
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="admin-nav-button" onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Admin stats
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="logout-button" onClick={doLogout} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 space-y-10">
        {/* Premium hero metric */}
        <div data-testid="dashboard-hero" className="relative overflow-hidden rounded-3xl border border-border bg-card p-8 sm:p-10">
          <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
              Hey {user?.name?.split(" ")[0] || "there"} — your deductions this month
            </p>
            <CountUp
              value={dashboard?.month_total || 0}
              className="font-heading text-5xl sm:text-6xl font-black tracking-tighter tabular-nums block"
            />
            <div className="flex flex-wrap gap-x-8 gap-y-1 mt-5 text-sm text-muted-foreground">
              <span>Year to date <b className="text-foreground font-heading">${Number(dashboard?.ytd_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></span>
              <span>All time <b className="text-foreground font-heading">${Number(dashboard?.all_time_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></span>
              <span>{dashboard?.receipt_count || 0} receipts logged</span>
            </div>
          </div>
        </div>

        {/* Usage meter (free tier) */}
        {usage && !usage.subscribed && (
          <div data-testid="usage-meter" className="rounded-2xl border border-border bg-secondary/40 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-2">
                <span className="font-semibold">{usage.month_count} / {usage.limit} free receipts this month</span>
                <span className="text-muted-foreground">{usage.remaining} left</span>
              </div>
              <Progress value={pct} className="h-2" />
            </div>
            <Button data-testid="usage-upgrade-button" onClick={() => navigate("/upgrade")} size="sm" className="rounded-xl bg-primary hover:bg-primary/90">
              Go unlimited
            </Button>
          </div>
        )}

        {/* Capture */}
        <section>
          {extracted ? (
            <ConfirmCard data={extracted} onSave={saveReceipt} onCancel={() => setExtracted(null)} saving={saving} />
          ) : (
            <ReceiptUpload onExtracted={setExtracted} />
          )}
        </section>

        {/* Forward by email */}
        <ForwardEmailCard />

        {/* Referral (Pro perk) */}
        {usage?.subscribed && <ReferralCard />}

        {/* Stats */}
        <Stats data={dashboard} />

        {/* Table */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold">Your expenses</h2>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-menu-button" variant="outline" className="rounded-xl">
                  <Download className="h-4 w-4 mr-2" /> Export <ChevronDown className="h-4 w-4 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-csv" onClick={() => exportFile("export", "schedule_c_expenses.csv", "CSV")}>
                  <FileText className="h-4 w-4 mr-2" /> CSV
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-xlsx" onClick={() => exportFile("export/xlsx", "schedule_c_expenses.xlsx", "Excel spreadsheet")}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-pdf" onClick={() => exportFile("export/pdf", "schedule_c_report.pdf", "PDF report")}>
                  <FileType className="h-4 w-4 mr-2" /> PDF report
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <ReceiptsTable receipts={receipts} onChanged={refresh} />
        </section>
      </main>
    </div>
  );
}
